import React, { useState, useEffect, useCallback } from 'react'
import { Table, Button, Drawer, Form, Select, Input, Switch, Tag, Space, Popconfirm, message as antMessage } from 'antd'
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons'
import api from '../../api'

const PLATFORMS = [
  { key: '*', name: '全平台' },
  { key: 'douyin', name: '抖音' },
  { key: 'kuaishou', name: '快手' },
  { key: 'xiaohongshu', name: '小红书' },
  { key: 'weibo', name: '微博' },
  { key: 'bilibili', name: 'B站' },
  { key: 'shipinhao', name: '视频号' },
  { key: 'jinritoutiao', name: '头条' }
]

function AutoReplyPanel() {
  const [rules, setRules] = useState([])
  const [templates, setTemplates] = useState([])
  const [loading, setLoading] = useState(false)
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [editingRule, setEditingRule] = useState(null)
  const [form] = Form.useForm()

  const loadData = useCallback(async () => {
    setLoading(true)
    const [rulesRes, templatesRes] = await Promise.all([
      api.listRules(),
      api.listTemplates()
    ])
    setRules(rulesRes.rules || [])
    setTemplates(templatesRes.templates || [])
    setLoading(false)
  }, [])

  useEffect(() => { loadData() }, [loadData])

  const openDrawer = (rule) => {
    setEditingRule(rule || null)
    if (rule) {
      form.setFieldsValue({
        name: rule.name,
        platform: rule.platform,
        scope: rule.scope,
        keywords: rule.keywords,
        matchType: rule.matchType,
        templateIds: rule.templateIds || []
      })
    } else {
      form.resetFields()
    }
    setDrawerOpen(true)
  }

  const handleSave = async () => {
    const values = await form.validateFields()
    values.keywords = (values.keywords || []).map(k => (typeof k === 'string' ? k : k))
    if (editingRule) values.rule_id = editingRule.rule_id
    const res = await api.saveRule(values)
    if (res.success) {
      antMessage.success('规则已保存')
      setDrawerOpen(false)
      loadData()
    } else {
      antMessage.error(res.message || '保存失败')
    }
  }

  const handleDelete = async (rule_id) => {
    await api.deleteRule(rule_id)
    antMessage.success('规则已删除')
    loadData()
  }

  const handleToggle = async (rule_id) => {
    await api.toggleRule(rule_id)
    loadData()
  }

  const columns = [
    { title: '规则名称', dataIndex: 'name', key: 'name' },
    { title: '平台', dataIndex: 'platform', key: 'platform', render: (v) => PLATFORMS.find(p => p.key === v)?.name || v },
    { title: '作用域', dataIndex: 'scope', key: 'scope', render: (v) => v === 'comment' ? '评论' : v === 'message' ? '私信' : '评论+私信' },
    { title: '关键词', dataIndex: 'keywords', key: 'keywords', render: (v) => (v || []).map(k => <Tag key={k}>{k}</Tag>) },
    { title: '匹配', dataIndex: 'matchType', key: 'matchType', render: (v) => v === 'exact' ? '精确' : v === 'regex' ? '正则' : '包含' },
    {
      title: '状态', dataIndex: 'enabled', key: 'enabled',
      render: (v, record) => <Switch checked={v} onChange={() => handleToggle(record.rule_id)} />
    },
    {
      title: '操作', key: 'actions',
      render: (_, record) => (
        <Space>
          <Button icon={<EditOutlined />} size="small" onClick={() => openDrawer(record)}>编辑</Button>
          <Popconfirm title="确定删除?" onConfirm={() => handleDelete(record.rule_id)}>
            <Button icon={<DeleteOutlined />} size="small" danger>删除</Button>
          </Popconfirm>
        </Space>
      )
    }
  ]

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => openDrawer(null)}>新建规则</Button>
      </div>
      <Table columns={columns} dataSource={rules} rowKey="rule_id" loading={loading} pagination={false} />

      <Drawer title={editingRule ? '编辑规则' : '新建规则'} open={drawerOpen} onClose={() => setDrawerOpen(false)} width={480}>
        <Form form={form} layout="vertical">
          <Form.Item name="name" label="规则名称" rules={[{ required: true }]}>
            <Input placeholder="如：产品咨询自动回复" />
          </Form.Item>
          <Form.Item name="platform" label="适用平台" initialValue="*">
            <Select options={PLATFORMS.map(p => ({ label: p.name, value: p.key }))} />
          </Form.Item>
          <Form.Item name="scope" label="作用范围" initialValue="comment">
            <Select options={[{ label: '评论', value: 'comment' }, { label: '私信', value: 'message' }, { label: '评论+私信', value: 'both' }]} />
          </Form.Item>
          <Form.Item name="keywords" label="触发关键词" rules={[{ required: true, message: '至少输入一个关键词' }]}>
            <Select mode="tags" placeholder="输入关键词后回车" open={false} />
          </Form.Item>
          <Form.Item name="matchType" label="匹配方式" initialValue="contains">
            <Select options={[{ label: '精确匹配', value: 'exact' }, { label: '包含匹配', value: 'contains' }, { label: '正则匹配', value: 'regex' }]} />
          </Form.Item>
          <Form.Item name="templateIds" label="回复话术">
            <Select mode="multiple" placeholder="选择话术模板" options={templates.map(t => ({ label: t.content.slice(0, 40) + (t.content.length > 40 ? '...' : ''), value: t.template_id }))} />
          </Form.Item>
          <Form.Item>
            <Button type="primary" onClick={handleSave}>保存</Button>
          </Form.Item>
        </Form>
      </Drawer>
    </div>
  )
}

export default AutoReplyPanel
